/**
 * ColumnConfigPopover — the `Columns` button that lets a user rearrange any table.
 *
 * A generic companion to `Blotter`: it renders a trigger styled like the blotter's Export button
 * (so the two sit flush in the `headerAction` slot) and a popover listing the configurable
 * columns, each with a checkbox, a drag handle and ↑/↓ buttons.
 *
 * It is deliberately **stateless about columns** — `order`, `hidden` and the four callbacks all
 * come from the caller (in practice from `useColumnPrefs`, which keeps them in the URL). The only
 * state it owns is whether it is open. A column that is always shown is not listed here at all;
 * say so with `fixedHint`.
 *
 * Focus management: `Popper` unmounts the panel whenever it closes, so both close paths (`Escape`
 * and clicking away) explicitly return focus to the trigger button — otherwise a keyboard user who
 * had tabbed into the panel would land on `document.body`, and the next Tab would restart from the
 * top of the page instead of continuing on from "Columns". Opening does the mirror move: focus
 * lands on the panel itself, which is what the trigger's `aria-haspopup="dialog"` and the panel's
 * own `role="dialog"` already promise a screen-reader user.
 */
import { closestCenter, DndContext, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DragIndicator, KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { Box, Button, Checkbox, ClickAwayListener, IconButton, Typography } from "@mui/material";
import Popper from "@mui/material/Popper";
import { useCallback, useEffect, useRef, useState } from "react";

/** One configurable column: the id used in the URL, and what the user reads. */
export interface ColumnConfigItem<T extends string> {
  id: T;
  label: string;
}

export interface ColumnConfigPopoverProps<T extends string> {
  /** Every configurable column. Ids not present in `order` are simply not rendered. */
  items: readonly ColumnConfigItem<T>[];
  order: readonly T[];
  hidden: readonly T[];
  /** One line naming the column the caller always shows; rendered above the list when given. */
  fixedHint?: string;
  onToggle: (id: T) => void;
  onMove: (id: T, delta: number) => void;
  onReorder: (from: number, to: number) => void;
  onReset: () => void;
}

/** Same border/height/typography as the blotter's Export button, so they read as a pair. */
const TRIGGER_SX = {
  border: "1px solid var(--color-dark-dust)",
  borderRadius: "var(--radius-btn)",
  color: "var(--color-dark-dust)",
  height: 48,
  px: 2,
  fontFamily: "var(--font-lato)",
  fontWeight: 600,
  fontSize: 13,
  letterSpacing: "0.08em",
  whiteSpace: "nowrap",
} as const;

const PANEL_SX = {
  bgcolor: "white",
  border: "1px solid var(--color-dark-dust)",
  borderRadius: "10px",
  boxShadow: "0px 4px 6px -1px rgba(0,0,0,0.1), 0px 2px 4px -2px rgba(0,0,0,0.1)",
  p: "8px",
  mt: "6px",
  width: 308,
  maxWidth: "calc(100vw - 32px)",
} as const;

/**
 * The panel's accessible name. Used for both its visible heading and its `aria-label` so the two
 * share one source and can never drift apart — previously each was its own hardcoded copy of the
 * same string.
 */
const PANEL_HEADING = "Configure columns";

interface ColumnConfigRowProps<T extends string> {
  id: T;
  label: string;
  isVisible: boolean;
  isFirst: boolean;
  isLast: boolean;
  onToggle: (id: T) => void;
  onMove: (id: T, delta: number) => void;
}

function ColumnConfigRow<T extends string>({
  id,
  label,
  isVisible,
  isFirst,
  isLast,
  onToggle,
  onMove,
}: ColumnConfigRowProps<T>) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  return (
    <Box
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        px: 0.5,
        borderRadius: "6px",
        bgcolor: "white",
        "&:hover": { bgcolor: "var(--color-light-dust)" },
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        aria-label={`Reorder ${label}`}
        sx={{
          display: "flex",
          alignItems: "center",
          color: "var(--color-dark-dust)",
          cursor: isDragging ? "grabbing" : "grab",
          touchAction: "none",
        }}
      >
        <DragIndicator fontSize="small" />
      </Box>

      <Checkbox
        checked={isVisible}
        onChange={() => onToggle(id)}
        size="small"
        sx={{ p: 0.5 }}
        inputProps={{ "aria-label": `Show ${label}` }}
      />

      <Typography
        sx={{
          flex: 1,
          fontFamily: "var(--font-lato)",
          fontSize: 14,
          color: "var(--color-rodeo-black)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>

      <IconButton
        size="small"
        onClick={() => onMove(id, -1)}
        disabled={isFirst}
        aria-label={`Move ${label} up`}
      >
        <KeyboardArrowUp fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => onMove(id, 1)}
        disabled={isLast}
        aria-label={`Move ${label} down`}
      >
        <KeyboardArrowDown fontSize="small" />
      </IconButton>
    </Box>
  );
}

export default function ColumnConfigPopover<T extends string>({
  items,
  order,
  hidden,
  fixedHint,
  onToggle,
  onMove,
  onReorder,
  onReset,
}: ColumnConfigPopoverProps<T>) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    // Gated on `open`: ClickAwayListener stays mounted with the trigger and fires on EVERY click
    // outside it, popover open or not. Ungated, this focused the trigger on every click anywhere
    // on the page — clicking the search box handed focus straight back here, so typing only
    // worked while the mouse stayed down (no mouseup, no click, no steal).
    setOpen((wasOpen) => {
      if (wasOpen) buttonRef.current?.focus();
      return false;
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") close();
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, close]);

  useEffect(() => {
    if (!open) return;
    // Focus the panel itself (its `tabIndex={-1}` below makes that a legal target), not the first
    // control inside it — today that's "Reset", not where a user opening the popover expects focus
    // to land.
    panelRef.current?.focus();
  }, [open]);

  // The list follows `order`; `items` only supplies the labels.
  const labels = new Map(items.map((item) => [item.id, item.label]));
  const rows = order.filter((id) => labels.has(id));
  const hiddenIds = new Set(hidden);
  // dnd-kit hands ids back as `string | number`, so positions are looked up by their string form.
  const positions = new Map<string, number>(
    order.map((id, index): [string, number] => [id, index]),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = positions.get(String(active.id));
    const to = positions.get(String(over.id));
    if (from === undefined || to === undefined) return;
    onReorder(from, to);
  }

  return (
    <ClickAwayListener onClickAway={close}>
      <Box sx={{ flexShrink: 0 }}>
        <Button
          ref={buttonRef}
          onClick={() => setOpen((previous) => !previous)}
          aria-expanded={open}
          aria-haspopup="dialog"
          // No ripple: switching windows mid-click leaves MUI's ripple with no exit animation to
          // run, so the shading stays painted on the button until the next interaction.
          disableRipple
          sx={TRIGGER_SX}
        >
          Columns
        </Button>

        <Popper
          open={open}
          anchorEl={buttonRef.current}
          placement="bottom-end"
          sx={{ zIndex: 1300 }}
        >
          <Box ref={panelRef} tabIndex={-1} role="dialog" aria-label={PANEL_HEADING} sx={PANEL_SX}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 0.5,
                pb: 0.5,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "var(--font-lato)",
                  fontWeight: 600,
                  fontSize: 13,
                  color: "var(--color-rodeo-black)",
                }}
              >
                {PANEL_HEADING}
              </Typography>
              <Button
                onClick={onReset}
                size="small"
                sx={{
                  fontFamily: "var(--font-lato)",
                  fontWeight: 600,
                  fontSize: 12,
                  color: "var(--color-dark-dust)",
                  minWidth: "auto",
                  px: 1,
                }}
              >
                Reset
              </Button>
            </Box>

            {fixedHint ? (
              <Typography
                sx={{
                  px: 0.5,
                  pb: 1,
                  fontFamily: "var(--font-lato)",
                  fontSize: 12,
                  color: "var(--color-dark-dust)",
                }}
              >
                {fixedHint}
              </Typography>
            ) : null}

            <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={rows} strategy={verticalListSortingStrategy}>
                {rows.map((id, index) => (
                  <ColumnConfigRow
                    key={id}
                    id={id}
                    label={labels.get(id) ?? id}
                    isVisible={!hiddenIds.has(id)}
                    isFirst={index === 0}
                    isLast={index === rows.length - 1}
                    onToggle={onToggle}
                    onMove={onMove}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </Box>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}
